import { reportCustomEvent } from '@/utils/arms';
import { readArmsFailDetail, truncateArmsFailDetail } from '@/utils/arms/common';
import { formatTrackerMoney } from '@/utils/tracker/pay/recharge-result';

import type { TRechargeGoodsType } from '@/utils/tracker/pay/recharge-result';

export const ARMS_PAYPAL_EVENT_TYPE = 'paypal' as const;
export const ARMS_PAYPAL_EVENT_GROUP = 'commerce' as const;

const PAYPAL_EVENT_NAME = {
    ATTEMPT: 'paypal_attempt',
    SUCCESS: 'paypal_success',
    FAIL: 'paypal_fail',
    V6_SDK_INIT: 'paypal_v6_sdk_init',
    V6_DEGRADE: 'paypal_v6_degrade',
    V6_SESSION_ERROR: 'paypal_v6_session_error',
} as const;

/** PayPal 出站渠道（perf / degrade 下钻） */
export type TPayPalV6CheckoutChannel =
    | 'v6_redirect'
    | 'checkout_url'
    | 'v6_sdk_not_ready'
    | 'v6_fallback_missing_order'
    | 'v6_fallback_sdk_unavailable'
    | 'v6_fallback_session_start';

/** PayPal 支付失败原因（供 Grafana 告警与下钻） */
export type TPayPalArmsFailReason =
    /** commerce/order/create/v1 失败 */
    | 'order_create_api_failed'
    /** 创单成功但缺少 checkout_url */
    | 'order_create_missing_checkout_url'
    /** PayPal 回跳 URL option=cancel（用户取消，未调 confirm） */
    | 'paypal_return_cancel'
    /** confirm 终态 FAILED */
    | 'order_confirm_failed'
    /** confirm 终态 CANCELED */
    | 'order_confirm_canceled'
    /** confirm 轮询结束仍为 PENDING */
    | 'order_confirm_pending_timeout'
    /** confirm 请求全程无成功响应 */
    | 'order_confirm_api_failed'
    /** v6 SDK 脚本加载或 createInstance 失败（支付前预加载阶段） */
    | 'v6_sdk_init_failed'
    /** v6 session.start 失败且无 checkout_url 可降级 */
    | 'v6_session_start_failed'
    /** v6 出站后 10s 内未离开页面（跳转被拦截或静默失败） */
    | 'v6_redirect_guard_timeout';

export interface IPayPalArmsTrackingProps {
    /** reader | pay */
    return_scene: string;
    goods_type: TRechargeGoodsType;
    gear_id: string;
    trigger_scene: string;
    paywall_template_id?: string;
    book_id?: string;
    chapter_id?: string;
    /** 最小货币单位（如美分），上报时转为美元小数 */
    money?: number;
}

export interface IReportPayPalSuccessProps extends IPayPalArmsTrackingProps {
    order_id: string;
}

export interface IReportPayPalFailProps extends IPayPalArmsTrackingProps {
    fail_reason: TPayPalArmsFailReason;
    fail_detail?: string;
    order_id?: string;
}

/** 创单失败时由 checkout redirect 传给 onError，仅用于 ARMS */
export interface IPayPalCheckoutArmsFailContext {
    fail_reason: TPayPalArmsFailReason;
    order_id?: string;
    fail_detail?: string;
}

function toArmsProperties(props: Record<string, string | number>): Record<string, string | number> {
    return {
        ...props,
        trigger_scene: props.trigger_scene || '',
        return_scene: props.return_scene || '',
    };
}

function sendPayPalArmsEvent(name: string, value: number, properties: Record<string, string | number>): void {
    reportCustomEvent(name, ARMS_PAYPAL_EVENT_TYPE, {
        group: ARMS_PAYPAL_EVENT_GROUP,
        value,
        properties: toArmsProperties(properties),
    });
}

function buildPayPalArmsPayload(props: IPayPalArmsTrackingProps): Record<string, string | number> {
    const payload: Record<string, string | number> = {
        return_scene: props.return_scene,
        goods_type: props.goods_type,
        gear_id: props.gear_id,
        trigger_scene: props.trigger_scene,
        paywall_template_id: props.paywall_template_id ?? '',
        book_id: props.book_id ?? '',
        chapter_id: props.chapter_id ?? '',
    };
    if (props.money !== undefined) {
        payload.money = formatTrackerMoney(props.money);
    }
    return payload;
}

/** 用户发起 PayPal 支付（创单前，成功率分母） */
export function reportPayPalAttempt(props: IPayPalArmsTrackingProps): void {
    sendPayPalArmsEvent(PAYPAL_EVENT_NAME.ATTEMPT, 1, buildPayPalArmsPayload(props));
}

/** confirm 终态 PAID */
export function reportPayPalSuccess(props: IReportPayPalSuccessProps): void {
    sendPayPalArmsEvent(PAYPAL_EVENT_NAME.SUCCESS, 1, {
        ...buildPayPalArmsPayload(props),
        order_id: props.order_id,
        result_status: 'success',
    });
}

/** 支付失败（用户 PayPal 取消、创单失败、confirm 非 PAID 等） */
export function reportPayPalFail(props: IReportPayPalFailProps): void {
    const payload: Record<string, string | number> = {
        ...buildPayPalArmsPayload(props),
        result_status: 'fail',
        fail_reason: props.fail_reason,
        ...(props.order_id ? { order_id: props.order_id } : {}),
        ...(props.fail_detail ? { fail_detail: truncateArmsFailDetail(props.fail_detail) } : {}),
    };
    sendPayPalArmsEvent(PAYPAL_EVENT_NAME.FAIL, 0, payload);
}

export function mapConfirmStatusToPayPalFailReason(status: string): TPayPalArmsFailReason {
    if (status === 'FAILED') {
        return 'order_confirm_failed';
    }
    if (status === 'CANCELED') {
        return 'order_confirm_canceled';
    }
    if (status === 'PENDING') {
        return 'order_confirm_pending_timeout';
    }
    return 'order_confirm_failed';
}

export function formatConfirmOrderFailDetail(data: { status: string; reason_code?: string }): string {
    const parts = [`status:${data.status}`];
    if (data.reason_code) {
        parts.push(`reason_code:${data.reason_code}`);
    }
    return parts.join(';');
}

/** 从 PayPal v6 SDK onError / start 异常中提取 error.code */
export function readPayPalV6ErrorCode(err: unknown): string | undefined {
    if (err && typeof err === 'object' && 'code' in err) {
        const code = (err as { code: unknown }).code;
        if (typeof code === 'string' && code.trim()) {
            return code.trim();
        }
    }
    return undefined;
}

export interface IReportPayPalV6SdkInitProps {
    success: 0 | 1;
    duration_ms: number;
    sandbox: 0 | 1;
    stage?: 'script_load' | 'no_create_instance' | 'create_instance';
    fail_detail?: string;
}

/** v6 SDK 预加载 / createInstance 初始化结果（成功或失败均上报） */
export function reportPayPalV6SdkInit(props: IReportPayPalV6SdkInitProps): void {
    const payload: Record<string, string | number> = {
        success: props.success,
        duration_ms: props.duration_ms,
        sandbox: props.sandbox,
        ...(props.stage ? { stage: props.stage } : {}),
        ...(props.fail_detail ? { fail_detail: truncateArmsFailDetail(props.fail_detail) } : {}),
    };
    sendPayPalArmsEvent(PAYPAL_EVENT_NAME.V6_SDK_INIT, props.success, payload);
}

export interface IReportPayPalV6DegradeProps {
    checkout_channel: TPayPalV6CheckoutChannel;
    order_id?: string;
    paypal_order_id?: string;
    page_path?: string;
    fail_detail?: string;
}

/** v6 不可用但 checkout_url 降级成功出站（不计入 paypal_fail） */
export function reportPayPalV6Degrade(props: IReportPayPalV6DegradeProps): void {
    const payload: Record<string, string | number> = {
        checkout_channel: props.checkout_channel,
        ...(props.order_id ? { order_id: props.order_id } : {}),
        ...(props.paypal_order_id ? { paypal_order_id: props.paypal_order_id } : {}),
        ...(props.page_path ? { page_path: props.page_path } : {}),
        ...(props.fail_detail ? { fail_detail: truncateArmsFailDetail(props.fail_detail) } : {}),
    };
    sendPayPalArmsEvent(PAYPAL_EVENT_NAME.V6_DEGRADE, 1, payload);
}

export interface IReportPayPalV6SessionErrorProps {
    stage: 'onError' | 'session_start';
    order_id?: string;
    paypal_order_id?: string;
    error_code?: string;
    fail_detail?: string;
}

/** v6 payment session 回调或 start 抛错（诊断用，可能与 degrade / fail 同时出现） */
export function reportPayPalV6SessionError(props: IReportPayPalV6SessionErrorProps): void {
    const payload: Record<string, string | number> = {
        stage: props.stage,
        ...(props.order_id ? { order_id: props.order_id } : {}),
        ...(props.paypal_order_id ? { paypal_order_id: props.paypal_order_id } : {}),
        ...(props.error_code ? { error_code: props.error_code } : {}),
        ...(props.fail_detail ? { fail_detail: truncateArmsFailDetail(props.fail_detail) } : {}),
    };
    sendPayPalArmsEvent(PAYPAL_EVENT_NAME.V6_SESSION_ERROR, 0, payload);
}

export { readArmsFailDetail };
